import { useEffect, useState } from 'react';

import { Plus } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import AppSideBar from '../../../ui-components/AppSideBar';
import AppTable from '../../../ui-components/AppTable';
import AppTooltip from '../../../ui-components/AppToolTip';
import Button from '../../../ui-components/Button';

import { useLazyPermissionListQuery } from './permissionApi';
import { setPermissionData } from './permissionSlice';

import type { PermissionRecord } from './model';
import type { RootState } from '../../../store';

export default function PermissionsPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [selectedGroup, setSelectedGroup] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');

  const [permissionListApi] = useLazyPermissionListQuery();

  const permission = useSelector((store: RootState) => store.permission);

  const handleAdd = (option: string) => {
    navigate(`/permissions/${option}`);
  };

  useEffect(() => {
    async function load() {
      const permissionRes = await permissionListApi().unwrap();
      const data: PermissionRecord[] = permissionRes?.data?.data?.records || [];
      dispatch(setPermissionData(data));
      const firstGroup = [...new Set(data.map((item) => item.group))][0];
      setSelectedGroup(firstGroup);
    }
    load();
  }, [dispatch, permissionListApi]);

  const dynamicSettings: PermissionRecord[] =
    (permission?.records as PermissionRecord[]) || [];
  const groupTypeArr: string[] = [
    ...new Set(dynamicSettings.map((item) => item.group)),
  ];

  const filteredGroupTypeArr = groupTypeArr.filter((group) =>
    group.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredSettings: PermissionRecord[] = dynamicSettings.filter(
    (item) => item.group === selectedGroup
  );

  return (
    <div className="bg-[#f9fafb] dark:bg-gray-800 flex flex-col card gap-6 h-[calc(100vh-9rem)]">
      <div className="flex flex-wrap justify-between items-center">
        <h2 className="top-title my-2">Permission List</h2>
        <AppTooltip title="Create Permission">
          <Button
            btnText={<Plus />}
            btnType="button"
            onClickFn={() => handleAdd('create')}
            customCss="btn-primary"
          />
        </AppTooltip>
      </div>
      <div className="flex flex-col sm:flex-row gap-4 overflow-y-auto">
        <div className="w-full sm:w-1/3 lg:w-1/4">
          <AppSideBar
            groupTypeArr={filteredGroupTypeArr}
            selectedGroup={selectedGroup}
            onSelect={setSelectedGroup}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            showSearch={true}
          />
        </div>
        <div className="w-full sm:w-2/3 lg:w-7/4">
          <div className="bg-white h-full dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow p-6 rounded-md">
            <AppTable
              data={filteredSettings as any}
              th1="Permission Name"
              th2="Permission Code"
              showAction={false}
              isNested={true}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
